import React, { useEffect, useRef, useState } from 'react'
import callAPI from '../../API/callSecureAPI'
import endpoints from '../../API/endpoints'
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Popover,
    Select,
    SvgIcon,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import { v4 as uuidv4 } from 'uuid'
import store from '../../redux/store'
import { useAuth0 } from '../../contexts/auth0-context'
import { useGetDraftKES } from '../../shared/queries/useGetDraftKES'
import { useGetSettingsQuery } from '../../shared/queries/useGetSettingsQuery'
import { useUpdateDraftMutation } from '../../shared/queries/useUpdateDraftMutation'
import { useSaveKESStatementsMutation } from '../../shared/queries/useSaveKESStatementsMutation'
import { useApproveKESMutation } from '../../shared/queries/useApproveKESMutation'
import { useDenyKESMutation } from '../../shared/queries/useDenyKESMutation'
import { useProcessEvidenceForMaiaMutation } from '../../shared/queries/useProcessEvidenceForMaiaMutation'
import useInvalidateLinkingQueries from '../../shared/queries/linking/useInvalidateLinkingQueries'
import { ReactComponent as ArrowDownIcon } from '../../assets/images/Arrow_Down_Icon.svg'
import { ReactComponent as ActionsIcon } from '../../assets/images/actions/actions.svg'
import { ReactComponent as AddCircleIcon } from '../../assets/images/add-circle.svg'
import { ReactComponent as CheckCircleIcon } from '../../assets/images/check-circle-1.svg'
import { ReactComponent as DenyIcon } from '../../assets/images/Close.svg'
import { PUBLICATIONS } from '../../shared/constants/publications'
import useEvidenceDisplayName from '../../shared/hooks/useDisplayNames/variants/useEvidenceDisplayName'

// ─── Types ───────────────────────────────────────────────────────────────────

export type BatchDraftAI = {
    title?: string
    abstract?: string
    year?: string | number
    authors?: string | string[]
    citation?: string
    shortCitation?: string
    longCitation?: string
    url?: string
    PMID?: string
    category?: string
    uniqueIdentifier?: string
}

export type BatchDraftResult = {
    draftId: string | null
    file: File | null // null for PubMed imports
    ai: BatchDraftAI
    editedValues: Record<string, any> | null
}

type KESItem = {
    id: string
    _id?: string
    statement: string
    figures: string[]
}

type Props = {
    open: boolean
    onClose: () => void
    initialResults: BatchDraftResult[]
    onSaved?: () => void
}

// ─── Constants ───────────────────────────────────────────────────────────────

const DRAFT_STATUS_DISPLAY: Record<string, string> = {
    draft: 'Draft',
    'in review': 'In Review',
    rejected: 'Rejected',
    accepted: 'Accepted',
}

const DRAFT_STATUS_ACTIONS = [
    { label: 'Accept', value: 'accepted' },
    { label: 'Mark as In Review', value: 'in review' },
    { label: 'Mark as Draft', value: 'draft' },
    { label: 'Rejected', value: 'rejected' },
]

const DRAFT_STATUS_BADGE_COLOR: Record<string, string> = {
    draft: 'figma.grayscale.tier4',
    'in review': 'figma.accentColors.orange',
    rejected: 'figma.accentColors.red',
    accepted: 'figma.accentColors.green',
}

// ─── Component ───────────────────────────────────────────────────────────────

const BatchDraftReviewDialog: React.FC<Props> = ({ open, onClose, initialResults, onSaved }) => {
    const { getTokenSilently } = useAuth0()
    const { data: settingsData } = useGetSettingsQuery()
    const { mutateAsync: updateDraftAsync } = useUpdateDraftMutation()
    const { mutateAsync: saveKESStatementsAsync } = useSaveKESStatementsMutation()
    const { mutateAsync: approveKESAsync } = useApproveKESMutation()
    const { mutateAsync: denyKESAsync } = useDenyKESMutation()
    const { mutate: processEvidenceForMaia } = useProcessEvidenceForMaiaMutation()
    const { invalidateLinkingQueries } = useInvalidateLinkingQueries()

    // Form fields
    const [evidenceName, setEvidenceName] = useState('')
    const [evidenceAbstract, setEvidenceAbstract] = useState('')
    const [evidenceYear, setEvidenceYear] = useState<number | string>(2025)
    const [evidenceIsInternal, setEvidenceIsInternal] = useState(false)
    const [evidenceCategory, setEvidenceCategory] = useState('---')
    const [evidenceCategories, setEvidenceCategories] = useState<string[]>([])
    const [author, setAuthor] = useState('')
    const [citation, setCitation] = useState('')
    const [shortCitation, setShortCitation] = useState('')
    const [longCitation, setLongCitation] = useState('')
    const [link, setLink] = useState('')
    const [pubmedId, setPubmedId] = useState('')
    const [uniqueIdentifier, setUniqueIdentifier] = useState('')
    const [evidencePopulation, setEvidencePopulation] = useState('---')
    const [publicationType, setPublicationType] = useState('---')
    const [draftStatus, setDraftStatus] = useState('draft')
    const [statusMenuAnchor, setStatusMenuAnchor] = useState<HTMLElement | null>(null)

    // Batch navigation
    const [results, setResults] = useState<BatchDraftResult[]>([])
    const [currentIndex, setCurrentIndex] = useState(0)
    const [isSaving, setIsSaving] = useState(false)

    // KES state
    const [localKES, setLocalKES] = useState<KESItem[]>([])
    const [batchPendingKES, setBatchPendingKES] = useState<any[]>([])
    const [batchPendingKESEdits, setBatchPendingKESEdits] = useState<
        Record<string, { statement: string; figures: string[] }>
    >({})
    const [batchKESLoading, setBatchKESLoading] = useState(false)
    const [pollingDraftId, setPollingDraftId] = useState<string | null>(null)
    const [kesPollingEnabled, setKesPollingEnabled] = useState(false)
    const kesAttemptRef = useRef(0)

    const evidenceDisplayName = useEvidenceDisplayName().plural

    // Sync evidence categories from settings
    useEffect(() => {
        if ((settingsData as any)?.evidenceCategories) {
            setEvidenceCategories((settingsData as any).evidenceCategories)
        }
    }, [settingsData])

    // Load first item when dialog opens with new results
    useEffect(() => {
        if (open && initialResults.length > 0) {
            setResults(initialResults)
            loadItemIntoForm(0, initialResults)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initialResults])

    // KES polling — fixed 4s interval, controlled by kesPollingEnabled
    const { data: kesData } = useGetDraftKES(pollingDraftId || '', kesPollingEnabled && !!pollingDraftId, 4000)

    useEffect(() => {
        if (!kesData || !pollingDraftId) return
        const approved = kesData.filter((k: any) => k.status === 'approved')
        const pending = kesData.filter((k: any) => k.status === 'pending')

        if (approved.length > 0) {
            setLocalKES(
                approved.map((k: any) => ({ id: k._id, _id: k._id, statement: k.statement, figures: k.figures || [] }))
            )
        }
        setBatchPendingKES(pending)
        setBatchPendingKESEdits(
            Object.fromEntries(pending.map((k: any) => [k._id, { statement: k.statement, figures: k.figures || [] }]))
        )

        if (pending.length > 0 || approved.length > 0) {
            // KES found — stop polling
            setBatchKESLoading(false)
            setKesPollingEnabled(false)
        } else {
            // No KES yet — count this attempt; give up after 12 tries (~48s)
            kesAttemptRef.current += 1
            if (kesAttemptRef.current >= 12) {
                setBatchKESLoading(false)
                setKesPollingEnabled(false)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kesData, pollingDraftId])

    // ─── Form loading ─────────────────────────────────────────────────────────

    function loadItemIntoForm(index: number, res: BatchDraftResult[]) {
        const { ai, editedValues: ev, draftId, file } = res[index]
        const resolvedName = ev ? ev.name : (ai.title || file?.name || '').replace(/\n+/gm, ' ')
        setEvidenceName(resolvedName)
        setEvidenceAbstract(ev ? ev.abstract : ai.abstract || '')
        setEvidenceYear(ev ? ev.year : ai.year ? ai.year.toString() : '2025')
        setEvidenceCategory(ev ? ev.category : ai.category || '---')
        setAuthor(ev ? ev.author : Array.isArray(ai.authors) ? ai.authors.join(', ') : ai.authors || '')
        setCitation(ev ? ev.citation : ai.citation || '')
        setShortCitation(ev ? ev.shortCitation || '' : ai.shortCitation || '')
        setLongCitation(ev ? ev.longCitation || '' : ai.longCitation || '')
        setLink(ev ? ev.link : ai.url || '')
        setPubmedId(ev ? ev.pubmedId : ai.PMID || '')
        setUniqueIdentifier(ev ? ev.uniqueIdentifier : ai.uniqueIdentifier || '')
        setEvidencePopulation(ev ? ev.population : '---')
        setPublicationType(ev ? ev.publicationType : '---')
        setDraftStatus(ev ? ev.status : 'draft')
        setEvidenceIsInternal(false)
        setLocalKES([])
        setBatchPendingKES([])
        setBatchPendingKESEdits({})
        setBatchKESLoading(false)
        setKesPollingEnabled(false)
        kesAttemptRef.current = 0
        setPollingDraftId(draftId || null)
        if (draftId) {
            setBatchKESLoading(true)
            setKesPollingEnabled(true)
        }
        setCurrentIndex(index)
    }

    function captureCurrentFormState() {
        return {
            name: evidenceName,
            abstract: evidenceAbstract,
            year: evidenceYear,
            category: evidenceCategory,
            author,
            citation: longCitation || citation,
            shortCitation,
            longCitation,
            link,
            pubmedId,
            uniqueIdentifier,
            population: evidencePopulation,
            publicationType,
            status: draftStatus,
        }
    }

    // ─── Navigation ───────────────────────────────────────────────────────────

    async function handleNavigate(index: number) {
        const currentEdits = captureCurrentFormState()
        const savedIndex = currentIndex

        const updatedResults = results.map((r, i) => (i === savedIndex ? { ...r, editedValues: currentEdits } : r))
        setResults(updatedResults)
        loadItemIntoForm(index, updatedResults)

        const draftId = updatedResults[savedIndex].draftId
        if (draftId) {
            updateDraftAsync({
                draftId,
                fields: {
                    name: currentEdits.name.replace(/\n+/gm, ' '),
                    abstract: currentEdits.abstract,
                    year: currentEdits.year,
                    category: currentEdits.category,
                    author: currentEdits.author,
                    citation: currentEdits.citation,
                    shortCitation: currentEdits.shortCitation,
                    longCitation: currentEdits.longCitation,
                    link: currentEdits.link,
                    pubmedId: currentEdits.pubmedId,
                    uniqueIdentifier: currentEdits.uniqueIdentifier,
                    population: currentEdits.population,
                    publicationType: currentEdits.publicationType,
                    status: currentEdits.status,
                },
            }).catch((err: unknown) => console.error('[Navigate] Background save failed:', err))
        }
    }

    // ─── Save / Accept ────────────────────────────────────────────────────────

    async function handleSaveCurrent(statusOverride?: string) {
        setIsSaving(true)
        try {
            const currentEdits = captureCurrentFormState()
            const draftId = results[currentIndex]?.draftId
            if (draftId) {
                const result = await updateDraftAsync({
                    draftId,
                    fields: {
                        name: currentEdits.name.replace(/\n+/gm, ' '),
                        abstract: currentEdits.abstract,
                        year: currentEdits.year,
                        category: currentEdits.category,
                        author: currentEdits.author,
                        citation: currentEdits.citation,
                        shortCitation: currentEdits.shortCitation,
                        longCitation: currentEdits.longCitation,
                        link: currentEdits.link,
                        pubmedId: currentEdits.pubmedId,
                        uniqueIdentifier: currentEdits.uniqueIdentifier,
                        population: currentEdits.population,
                        publicationType: currentEdits.publicationType,
                        status: statusOverride ?? currentEdits.status,
                    },
                })
                if (statusOverride === 'accepted') {
                    invalidateLinkingQueries()
                    if (result?.evidenceId && result?.data?.s3Name) {
                        processEvidenceForMaia({ evidenceId: result.evidenceId })
                    }
                }
                await saveKESStatementsAsync({ draftId, localKES: localKES as any })
            }
        } catch (err) {
            console.error('[SaveDraft] Failed:', err)
        } finally {
            setIsSaving(false)
            onSaved?.()
            const next = currentIndex + 1
            if (next < results.length) {
                loadItemIntoForm(next, results)
            } else {
                onClose()
            }
        }
    }

    const handleAccept = () => handleSaveCurrent('accepted')

    async function handleRemoveFromImport() {
        const item = results[currentIndex]
        if (item?.draftId) {
            const token = await getTokenSilently?.({ audience: 'https://atlas.aesara.com' })
            callAPI(endpoints.deleteDraft.url + item.draftId, endpoints.deleteDraft.method, token)
        }
        const updated = results.filter((_, i) => i !== currentIndex)
        if (updated.length === 0) {
            onSaved?.()
            onClose()
            return
        }
        const nextIndex = Math.min(currentIndex, updated.length - 1)
        setResults(updated)
        loadItemIntoForm(nextIndex, updated)
    }

    async function handleSaveAll() {
        setIsSaving(true)
        try {
            const currentEdits = captureCurrentFormState()
            const draftId = results[currentIndex]?.draftId
            if (draftId) {
                await updateDraftAsync({
                    draftId,
                    fields: {
                        name: currentEdits.name.replace(/\n+/gm, ' '),
                        abstract: currentEdits.abstract,
                        year: currentEdits.year,
                        category: currentEdits.category,
                        author: currentEdits.author,
                        citation: currentEdits.citation,
                        shortCitation: currentEdits.shortCitation,
                        longCitation: currentEdits.longCitation,
                        link: currentEdits.link,
                        pubmedId: currentEdits.pubmedId,
                        uniqueIdentifier: currentEdits.uniqueIdentifier,
                        population: currentEdits.population,
                        publicationType: currentEdits.publicationType,
                        status: currentEdits.status,
                    },
                })
                await saveKESStatementsAsync({ draftId, localKES: localKES as any })
            }
        } catch (err) {
            console.error('[SaveAll] Failed:', err)
        } finally {
            setIsSaving(false)
            onSaved?.()
            onClose()
        }
    }

    // ─── KES handlers ─────────────────────────────────────────────────────────

    function handleAddKES() {
        setLocalKES((prev) => [...prev, { id: uuidv4(), statement: '', figures: [] }])
    }

    function handleKESTextChange(id: string, text: string) {
        setLocalKES((prev) => prev.map((k) => (k.id === id ? { ...k, statement: text } : k)))
    }

    function handleKESFileSelect(e: React.ChangeEvent<HTMLInputElement>, id: string) {
        const files = Array.from(e.target.files || []).filter((f) => {
            const ext = f.name.split('.').pop()?.toLowerCase()
            return ext === 'png' || ext === 'jpg' || ext === 'jpeg'
        })
        const readers = files.map(
            (f) =>
                new Promise<string>((resolve, reject) => {
                    const reader = new FileReader()
                    reader.readAsDataURL(f)
                    reader.onload = () => resolve(reader.result as string)
                    reader.onerror = reject
                })
        )
        Promise.all(readers).then((base64Array) => {
            setLocalKES((prev) =>
                prev.map((k) => (k.id === id ? { ...k, figures: [...k.figures, ...base64Array] } : k))
            )
        })
    }

    function handleBatchPendingKESTextChange(id: string, text: string) {
        setBatchPendingKESEdits((prev) => ({ ...prev, [id]: { ...prev[id], statement: text } }))
    }

    function handleBatchPendingKESFileSelect(e: React.ChangeEvent<HTMLInputElement>, id: string) {
        const files = Array.from(e.target.files || []).filter((f) => {
            const ext = f.name.split('.').pop()?.toLowerCase()
            return ext === 'png' || ext === 'jpg' || ext === 'jpeg'
        })
        const readers = files.map(
            (f) =>
                new Promise<string>((resolve, reject) => {
                    const reader = new FileReader()
                    reader.readAsDataURL(f)
                    reader.onload = () => resolve(reader.result as string)
                    reader.onerror = reject
                })
        )
        Promise.all(readers).then((base64Array) => {
            setBatchPendingKESEdits((prev) => ({
                ...prev,
                [id]: { ...prev[id], figures: [...(prev[id]?.figures || []), ...base64Array] },
            }))
        })
    }

    async function handleBatchApproveKES(id: string) {
        try {
            const edit = batchPendingKESEdits[id]
            await approveKESAsync({
                id,
                payload: {
                    ...(edit?.statement !== undefined && { statement: edit.statement }),
                    ...(edit?.figures !== undefined && { figures: edit.figures }),
                },
            })
            const approved = batchPendingKES.find((k: any) => k._id === id)
            if (approved) {
                setLocalKES((prev) => [
                    ...prev,
                    {
                        id: uuidv4(),
                        _id: approved._id,
                        statement: edit?.statement ?? approved.statement,
                        figures: edit?.figures ?? approved.figures ?? [],
                    },
                ])
            }
            setBatchPendingKES((prev) => prev.filter((k: any) => k._id !== id))
        } catch (err) {
            console.error('Failed to approve KES:', err)
        }
    }

    async function handleBatchDenyKES(id: string) {
        try {
            await denyKESAsync({ id })
            setBatchPendingKES((prev) => prev.filter((k: any) => k._id !== id))
        } catch (err) {
            console.error('Failed to deny KES:', err)
        }
    }

    // ─── Render ───────────────────────────────────────────────────────────────

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" aria-labelledby="batch-review-dialog-title">
            <DialogContent dividers sx={{ overflowX: 'hidden' }}>
                {isSaving ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 2,
                            py: 8,
                        }}
                    >
                        <CircularProgress />
                        <Typography variant="16x400" color="figma.grayscale.textHier2">
                            Saving…
                        </Typography>
                    </Box>
                ) : (
                    <>
                        {/* Header — direct DialogContent child so width is always fully constrained */}
                        {(() => {
                            const cur = results[currentIndex]
                            const isLocal = !!cur?.file
                            const sourceLabel = isLocal ? 'Local Files' : 'PubMed'
                            const displayName = cur ? (isLocal ? cur.file!.name : cur.ai.title || '') : ''
                            return (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', mb: 1 }}>
                                    {/* Title row */}
                                    <Box
                                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                                    >
                                        <Typography variant="22x600" color="figma.grayscale.textHier1">
                                            Reviewing Evidence
                                        </Typography>
                                        <IconButton size="small" onClick={onClose}>
                                            <DenyIcon style={{ width: 20, height: 20 }} />
                                        </IconButton>
                                    </Box>
                                    <Divider />

                                    {/* Source row */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="18x500" color="figma.grayscale.textHier1">
                                            Source:
                                        </Typography>
                                        <Typography variant="18x400" color="figma.grayscale.textHier2">
                                            {sourceLabel}
                                        </Typography>
                                    </Box>

                                    {/* File name + status badge + actions */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {displayName && (
                                            <Typography
                                                variant="18x500"
                                                color="figma.grayscale.textHier1"
                                                sx={{
                                                    flex: 1,
                                                    minWidth: 0,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {displayName}
                                            </Typography>
                                        )}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                padding: '4px 12px',
                                                alignItems: 'center',
                                                gap: '4px',
                                                borderRadius: '30px',
                                                bgcolor:
                                                    DRAFT_STATUS_BADGE_COLOR[draftStatus] ?? 'figma.grayscale.tier4',
                                                flexShrink: 0,
                                            }}
                                        >
                                            <Typography variant="14x500" color="figma.grayscale.contentWrapper">
                                                {DRAFT_STATUS_DISPLAY[draftStatus] ?? draftStatus}
                                            </Typography>
                                        </Box>
                                        <IconButton size="small" onClick={(e) => setStatusMenuAnchor(e.currentTarget)}>
                                            <ActionsIcon style={{ width: 20, height: 20 }} />
                                        </IconButton>
                                        <Popover
                                            open={Boolean(statusMenuAnchor)}
                                            anchorEl={statusMenuAnchor}
                                            onClose={() => setStatusMenuAnchor(null)}
                                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                            slotProps={{
                                                paper: {
                                                    sx: {
                                                        display: 'inline-flex',
                                                        padding: '12px',
                                                        flexDirection: 'column',
                                                        justifyContent: 'center',
                                                        alignItems: 'flex-start',
                                                        gap: '10px',
                                                        borderRadius: '12px',
                                                        bgcolor: 'figma.grayscale.tier7',
                                                        boxShadow: '0 4px 8px 0 rgba(0,0,0,0.20)',
                                                    },
                                                },
                                            }}
                                        >
                                            {DRAFT_STATUS_ACTIONS.map(({ label, value }) => (
                                                <Box
                                                    key={value}
                                                    onClick={() => {
                                                        setDraftStatus(value)
                                                        setStatusMenuAnchor(null)
                                                    }}
                                                    sx={{
                                                        cursor: 'pointer',
                                                        padding: '4px 8px',
                                                        borderRadius: '6px',
                                                        width: '100%',
                                                        '&:hover': { bgcolor: 'figma.grayscale.tier6' },
                                                    }}
                                                >
                                                    <Typography variant="14x400" color="figma.grayscale.textHier2">
                                                        {label}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Popover>
                                    </Box>
                                </Box>
                            )
                        })()}

                        {/* Paginator — direct DialogContent child so centering is always against full dialog width */}
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: '16px',
                                py: '4px',
                                mb: 1,
                            }}
                        >
                            <IconButton
                                size="small"
                                disabled={currentIndex === 0}
                                onClick={() => handleNavigate(currentIndex - 1)}
                            >
                                <ArrowDownIcon style={{ width: 20, height: 20, transform: 'rotate(90deg)' }} />
                            </IconButton>
                            <Typography variant="16x400" color="figma.grayscale.textHier2">
                                Imported: {results.length} {results.length === 1 ? 'item' : 'items'}
                            </Typography>
                            <Box sx={{ width: '1px', height: '24px', bgcolor: 'figma.grayscale.textHier2' }} />
                            <Typography variant="16x400" color="figma.grayscale.textHier2">
                                Item: {currentIndex + 1}/{results.length}
                            </Typography>
                            <IconButton
                                size="small"
                                disabled={currentIndex === results.length - 1}
                                onClick={() => handleNavigate(currentIndex + 1)}
                            >
                                <ArrowDownIcon style={{ width: 20, height: 20, transform: 'rotate(-90deg)' }} />
                            </IconButton>
                        </Box>
                        <Divider sx={{ mb: 1 }} />

                        <Grid container direction="column" spacing={1}>
                            {/* Internal/External toggle */}
                            <Grid item>
                                <FormControl>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={evidenceIsInternal}
                                                onChange={(e) => setEvidenceIsInternal(e.target.checked)}
                                                name="Internal/External"
                                            />
                                        }
                                        label="External"
                                    />
                                </FormControl>
                            </Grid>

                            {/* Title */}
                            <Grid item>
                                <TextField
                                    margin="dense"
                                    id="evidence-name"
                                    label={`${evidenceDisplayName} Title`}
                                    type="text"
                                    fullWidth
                                    multiline
                                    rows={4}
                                    value={evidenceName}
                                    onFocus={(e) => e.target.setAttribute('autocomplete', 'off')}
                                    onChange={(e) => setEvidenceName(e.target.value)}
                                />
                            </Grid>

                            {/* Unique Study Identifier + PubMed ID */}
                            <Grid container item spacing={1}>
                                <Grid item xs>
                                    <TextField
                                        id="usi"
                                        margin="dense"
                                        label="Unique Study Identifier"
                                        type="text"
                                        fullWidth
                                        value={uniqueIdentifier}
                                        onFocus={(e) => e.target.setAttribute('autocomplete', 'off')}
                                        onChange={(e) => setUniqueIdentifier(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs>
                                    <TextField
                                        id="pmid"
                                        margin="dense"
                                        label="PubMed ID"
                                        type="text"
                                        fullWidth
                                        value={pubmedId}
                                        onFocus={(e) => e.target.setAttribute('autocomplete', 'off')}
                                        onChange={(e) => setPubmedId(e.target.value)}
                                    />
                                </Grid>
                            </Grid>

                            {/* Key Points (abstract) */}
                            <Grid item>
                                <TextField
                                    margin="dense"
                                    label="Key Points"
                                    helperText="*Generated with ATLAS AI - please verify accuracy."
                                    type="text"
                                    fullWidth
                                    multiline
                                    rows={4}
                                    value={evidenceAbstract}
                                    onFocus={(e) => e.target.setAttribute('autocomplete', 'off')}
                                    onChange={(e) => setEvidenceAbstract(e.target.value)}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            resize: 'vertical',
                                            overflow: 'hidden',
                                            minHeight: '120px',
                                        },
                                        '& textarea': {
                                            resize: 'none',
                                            overflow: 'auto !important',
                                            height: '100% !important',
                                        },
                                    }}
                                />
                            </Grid>

                            {/* Short Citation */}
                            <Grid item>
                                <TextField
                                    margin="dense"
                                    label="Short Citation"
                                    type="text"
                                    fullWidth
                                    value={shortCitation}
                                    onFocus={(e) => e.target.setAttribute('autocomplete', 'off')}
                                    onChange={(e) => setShortCitation(e.target.value)}
                                />
                            </Grid>

                            {/* Long Citation */}
                            <Grid item>
                                <TextField
                                    margin="dense"
                                    label="Long Citation"
                                    type="text"
                                    fullWidth
                                    multiline
                                    rows={2}
                                    value={longCitation}
                                    onFocus={(e) => e.target.setAttribute('autocomplete', 'off')}
                                    onChange={(e) => setLongCitation(e.target.value)}
                                />
                            </Grid>

                            {/* Author(s) */}
                            <Grid item>
                                <TextField
                                    margin="dense"
                                    id="evidence-author"
                                    label="Author(s)"
                                    type="text"
                                    fullWidth
                                    value={author}
                                    onFocus={(e) => e.target.setAttribute('autocomplete', 'off')}
                                    onChange={(e) => setAuthor(e.target.value)}
                                />
                            </Grid>

                            {/* Population + Publication Type */}
                            <Grid container item spacing={1}>
                                <Grid item xs>
                                    <FormControl fullWidth margin="dense">
                                        <InputLabel htmlFor="outlined-age-native-evidence-pop">Population</InputLabel>
                                        <Select
                                            label="POPULATION"
                                            inputProps={{ id: 'outlined-age-native-simple-pop' }}
                                            value={evidencePopulation}
                                            onChange={(e) => setEvidencePopulation(e.target.value as string)}
                                        >
                                            <MenuItem value="---">---</MenuItem>
                                            {((store.getState() as any)?.settings?.vmSubpopulations || []).map(
                                                (cat: string, i: number) => (
                                                    <MenuItem value={cat} key={cat + i}>
                                                        {cat}
                                                    </MenuItem>
                                                )
                                            )}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs>
                                    <FormControl fullWidth margin="dense">
                                        <InputLabel htmlFor="outlined-age-native-evidence-pub-type">
                                            Publication Type
                                        </InputLabel>
                                        <Select
                                            label="PUBLICATION TYPE"
                                            inputProps={{ id: 'outlined-age-native-simple-pub-type' }}
                                            value={publicationType}
                                            onChange={(e) => setPublicationType(e.target.value as string)}
                                        >
                                            <MenuItem value="---">---</MenuItem>
                                            {Object.values(PUBLICATIONS).map((cat, i) => (
                                                <MenuItem value={cat} key={cat + i}>
                                                    {cat}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>

                            {/* Year + Category */}
                            <Grid container item spacing={1}>
                                <Grid item xs>
                                    <TextField
                                        id="year"
                                        margin="dense"
                                        label="Year"
                                        type="number"
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                        value={evidenceYear}
                                        onFocus={(e) => e.target.setAttribute('autocomplete', 'off')}
                                        onChange={(e) => setEvidenceYear(Number.parseInt(e.target.value))}
                                    />
                                </Grid>
                                <Grid item xs>
                                    <FormControl fullWidth margin="dense">
                                        <InputLabel htmlFor="outlined-age-native-evidence-category">
                                            Category
                                        </InputLabel>
                                        <Select
                                            label="CATEGORY"
                                            inputProps={{ id: `outlined-age-native-simple-${evidenceCategory}` }}
                                            value={evidenceCategory}
                                            onChange={(e) => setEvidenceCategory(e.target.value as string)}
                                        >
                                            <MenuItem value="---">---</MenuItem>
                                            {evidenceCategories.map((cat, i) => (
                                                <MenuItem value={cat} key={cat + i}>
                                                    {cat}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>

                            {/* URL */}
                            <Grid item>
                                <TextField
                                    margin="dense"
                                    label="URL"
                                    type="text"
                                    fullWidth
                                    value={link}
                                    placeholder="http://"
                                    onFocus={(e) => e.target.setAttribute('autocomplete', 'off')}
                                    onChange={(e) => setLink(e.target.value)}
                                />
                            </Grid>

                            {/* Manual KES entry */}
                            <Grid item>
                                <Box
                                    sx={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', mt: 1 }}
                                    onClick={handleAddKES}
                                >
                                    <AddCircleIcon style={{ width: 20, height: 20 }} />
                                    <Typography variant="16x400" color="figma.grayscale.textHier1">
                                        Add Key Evidence Statements
                                    </Typography>
                                </Box>
                            </Grid>
                            {localKES.map((kes, index) => (
                                <Grid item key={kes.id}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            padding: '16px',
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                            gap: '10px',
                                            alignSelf: 'stretch',
                                            borderRadius: '12px',
                                            border: '1px solid',
                                            borderColor: 'figma.grayscale.stroke',
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}>
                                            <Typography
                                                variant="22x300"
                                                color="figma.grayscale.textHier2"
                                                sx={{ whiteSpace: 'nowrap' }}
                                            >
                                                {index + 1}.
                                            </Typography>
                                            <TextField
                                                variant="standard"
                                                placeholder="Enter here"
                                                value={kes.statement}
                                                onChange={(e) => handleKESTextChange(kes.id, e.target.value)}
                                                multiline
                                                fullWidth
                                                InputProps={{ disableUnderline: true }}
                                                sx={{
                                                    flex: '1 0 0',
                                                    '& .MuiInputBase-input': { color: 'figma.grayscale.textHier2' },
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                    <IconButton component={'label' as any} sx={{ mt: 0.5, borderRadius: 1 }}>
                                        <input
                                            type="file"
                                            multiple
                                            hidden
                                            accept=".png,.jpg,.jpeg"
                                            onChange={(e) => handleKESFileSelect(e, kes.id)}
                                        />
                                        <Typography
                                            variant="16x600"
                                            color="primary.main"
                                            sx={{ textTransform: 'uppercase' }}
                                        >
                                            Attach Figure{kes.figures.length > 0 && ` (${kes.figures.length})`}
                                        </Typography>
                                    </IconButton>
                                </Grid>
                            ))}

                            {/* AI-Recommended KES */}
                            <Grid item>
                                <Divider sx={{ mt: 1 }} />
                            </Grid>
                            <Grid item>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <AutoAwesomeIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                        AI-Recommended Key Evidence Statements
                                    </Typography>
                                </Box>
                            </Grid>
                            {batchKESLoading ? (
                                <Grid item>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CircularProgress size={16} />
                                        <Typography variant="body2" color="text.secondary">
                                            Generating statements…
                                        </Typography>
                                    </Box>
                                </Grid>
                            ) : (
                                batchPendingKES.map((kes: any, index: number) => (
                                    <Grid item key={kes._id}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                padding: '16px',
                                                flexDirection: 'column',
                                                alignItems: 'flex-start',
                                                gap: '10px',
                                                alignSelf: 'stretch',
                                                borderRadius: '12px',
                                                border: '1px solid',
                                                borderColor: 'figma.grayscale.stroke',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'flex-start',
                                                    gap: 1,
                                                    width: '100%',
                                                }}
                                            >
                                                <Typography
                                                    variant="22x300"
                                                    color="figma.grayscale.textHier2"
                                                    sx={{ whiteSpace: 'nowrap' }}
                                                >
                                                    {localKES.length + index + 1}.
                                                </Typography>
                                                <TextField
                                                    variant="standard"
                                                    placeholder="Enter here"
                                                    value={batchPendingKESEdits[kes._id]?.statement ?? kes.statement}
                                                    onChange={(e) =>
                                                        handleBatchPendingKESTextChange(kes._id, e.target.value)
                                                    }
                                                    multiline
                                                    fullWidth
                                                    InputProps={{ disableUnderline: true }}
                                                    sx={{
                                                        flex: '1 0 0',
                                                        '& .MuiInputBase-input': { color: 'figma.grayscale.textHier2' },
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                mt: 0.5,
                                            }}
                                        >
                                            <IconButton component={'label' as any} sx={{ borderRadius: 1 }}>
                                                <input
                                                    type="file"
                                                    multiple
                                                    hidden
                                                    accept=".png,.jpg,.jpeg"
                                                    onChange={(e) => handleBatchPendingKESFileSelect(e, kes._id)}
                                                />
                                                <Typography
                                                    variant="16x600"
                                                    color="primary.main"
                                                    sx={{ textTransform: 'uppercase' }}
                                                >
                                                    Attach Figure
                                                    {(batchPendingKESEdits[kes._id]?.figures?.length ?? 0) > 0 &&
                                                        ` (${batchPendingKESEdits[kes._id].figures.length})`}
                                                </Typography>
                                            </IconButton>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                <Tooltip title="Deny" arrow>
                                                    <IconButton
                                                        onClick={() => handleBatchDenyKES(kes._id)}
                                                        size="small"
                                                    >
                                                        <SvgIcon
                                                            component={DenyIcon}
                                                            inheritViewBox
                                                            sx={{ color: 'figma.grayscale.textHier2', fontSize: 22 }}
                                                        />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Approve" arrow>
                                                    <IconButton
                                                        onClick={() => handleBatchApproveKES(kes._id)}
                                                        size="small"
                                                    >
                                                        <SvgIcon
                                                            component={CheckCircleIcon}
                                                            inheritViewBox
                                                            sx={{ color: 'primary.main', fontSize: 22 }}
                                                        />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                    </Grid>
                                ))
                            )}
                        </Grid>
                    </>
                )}
            </DialogContent>

            {!isSaving && (
                <DialogActions sx={{ justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button onClick={handleRemoveFromImport} variant="outlined" color="error" disabled={isSaving}>
                            Remove from Import
                        </Button>
                        <Button onClick={handleSaveAll} variant="outlined" color="primary" disabled={isSaving}>
                            Save All to Drafts
                        </Button>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            onClick={() => handleSaveCurrent()}
                            variant="outlined"
                            color="primary"
                            disabled={isSaving}
                        >
                            Save
                        </Button>
                        <Button onClick={handleAccept} variant="contained" color="primary" disabled={isSaving}>
                            Accept to Evidence
                        </Button>
                    </Box>
                </DialogActions>
            )}
        </Dialog>
    )
}

export default BatchDraftReviewDialog
